import { SwaggerFile } from '../../types/swaggerFile';

const SIZE_UNITS = ['Bytes', 'KB', 'MB', 'GB'];

export function formatFileSize(bytes: number): string {
  if (!bytes) return '0 Bytes';
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), SIZE_UNITS.length - 1);
  return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(2))} ${SIZE_UNITS[i]}`;
}

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** Split an upload timestamp into a relative day label and a short time, e.g. `{ day: 'Today', time: '1:20 PM' }`. */
export function formatUploaded(value: string, now: Date = new Date()): { day: string; time: string } {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { day: '—', time: '' };
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const day = isSameDay(date, now)
    ? 'Today'
    : isSameDay(date, yesterday)
      ? 'Yesterday'
      : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return { day, time: date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) };
}

/** Case-insensitive match against name, description and tags. */
export function matchesQuery(file: SwaggerFile, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [file.originalName, file.description ?? '', ...(file.tags ?? [])].some((field) => field.toLowerCase().includes(q));
}

/** Tags are stored comma-joined on upload, so commas can't be part of a tag. */
export const normalizeTag = (raw: string) => raw.replace(/,/g, '').trim().toLowerCase();

const DEFAULT_SUGGESTIONS = ['production', 'sandbox', 'omnibus', 'carrier-selection', 'labels', 'tracking'];

/** Most-used existing tags first, padded with defaults, excluding tags already chosen. */
export function suggestTags(files: SwaggerFile[], chosen: string[], limit = 4): string[] {
  const counts = new Map<string, number>();
  for (const file of files) for (const tag of file.tags ?? []) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  const ranked = Array.from(counts.keys()).sort((a, b) => counts.get(b)! - counts.get(a)!);
  const taken = new Set(chosen);
  const out: string[] = [];
  for (const tag of [...ranked, ...DEFAULT_SUGGESTIONS]) {
    if (out.length === limit) break;
    if (!taken.has(tag)) {
      taken.add(tag);
      out.push(tag);
    }
  }
  return out;
}

const HTTP_METHODS = new Set(['get', 'put', 'post', 'delete', 'options', 'head', 'patch', 'trace']);

export type SpecInspection = { valid: true; summary: string } | { valid: false; error: string };

/** Client-side preflight mirroring the backend's upload checks (`.json`, parseable, has `openapi`/`swagger`). */
export function inspectSpec(name: string, text: string, size: number): SpecInspection {
  if (!name.toLowerCase().endsWith('.json')) return { valid: false, error: 'Only .json files are supported' };
  let spec: any;
  try {
    spec = JSON.parse(text);
  } catch {
    return { valid: false, error: 'Not valid JSON' };
  }
  const version = spec?.openapi ? `OpenAPI ${spec.openapi}` : spec?.swagger ? `Swagger ${spec.swagger}` : null;
  if (!version) return { valid: false, error: 'Not an OpenAPI/Swagger specification' };
  let operations = 0;
  for (const item of Object.values<any>(spec.paths ?? {})) {
    if (item && typeof item === 'object') operations += Object.keys(item).filter((key) => HTTP_METHODS.has(key)).length;
  }
  return { valid: true, summary: `Valid ${version} · ${formatFileSize(size)} · ${operations} operation${operations === 1 ? '' : 's'}` };
}

export function errorMessage(err: any, fallback: string): string {
  return err?.response?.data?.message || fallback;
}
